import { sendAnalytics } from '@audio-network/analytics'
import {
	Card,
	CardList,
	CardListSkeleton,
	CardSkeleton,
	Container,
	Heading,
	PlaceholderAlbumArtwork,
	Skeleton,
} from '@audio-network/componentlibrary'
import { usePlayer } from '@audio-network/player'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import React from 'react'
import {
	Configure,
	ConfigureProps,
	Index,
	useHits,
	useInfiniteHits,
	useInstantSearch,
	useSortBy,
	useStats,
} from 'react-instantsearch'

import {
	getAlbumRestrictedAttributes,
	mapTracksForAiSearch,
} from '@/components/pages/search/Results/Results.utils'
import { isMostRecentSort, SORT_OPTIONS } from '@/constants/search'
import { CARD_LIST_THUMBNAIL_MAX_WIDTH } from '@/constants/sizing'
import { getLyricsRestrictedAttributes } from '@/pages/track/searchkeyword/restrictedTrackAttributesHelper'
import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'
import optimizeContentfulImage from '@/utils/contentful/optimizeContentfulImage'
import mapAlgoliaAlbumToAlbum from '@/utils/music/mapAlgoliaAlbumToAlbum'
import { getAlbumUrl } from '@/utils/urls'

import styles from './AlbumResults.module.css'
import { AlbumResultsProps } from './AlbumResults.types'

const AlbumResults = ({
	trackResults,
	nbTrackHits,
}: AlbumResultsProps): JSX.Element => {
	const { status } = useInstantSearch()
	const { items } = useHits()
	const { aiSearchPending, aiSearchResults, aiSearchHasFailed } =
		useAiSearchStore()
	const { currentRefinement } = useSortBy(SORT_OPTIONS)
	const {
		togglePlay,
		isPlaying: isPlayingFromPlayer,
		currentTrack,
	} = usePlayer()
	const { t } = useTranslation(['search'])

	const shouldUseAiSearchRanking =
		aiSearchResults && !isMostRecentSort(currentRefinement)

	const mappedTracks = mapTracksForAiSearch(
		trackResults,
		shouldUseAiSearchRanking ? aiSearchResults : null
	)

	/*
	 * Order albums by relevancy suggested by AI provider.
	 *
	 * Because there is no correlation currently between AI order (just track ids)
	 * and the album results from Algolia (just album ids)
	 * we must use our track results from Algolia to determine order
	 */
	const orderedAlbums = shouldUseAiSearchRanking
		? Array.from(
				new Set(
					mappedTracks
						.map((track) =>
							items.find((item) => item.Album.Id === track.album.number)
						)
						.filter((item) => item)
				)
		  )
		: items
	const albums = orderedAlbums.map((album) =>
		mapAlgoliaAlbumToAlbum(
			album.Album,
			mappedTracks.find((track) => track.album.number === album.Album.Id)
		)
	)

	const aiResultsNoSearchHits =
		!aiSearchHasFailed && aiSearchResults && nbTrackHits === 0

	const areTrackAiResultsOutOfSync =
		aiSearchResults &&
		trackResults.length > 0 &&
		!trackResults.every((trackResult) =>
			aiSearchResults.some(
				(aiSearchResult) => trackResult.Id === Number(aiSearchResult.id)
			)
		)

	const showSkeletons =
		(items.length > 0 && albums.length === 0) ||
		status !== 'idle' ||
		aiSearchPending ||
		areTrackAiResultsOutOfSync

	if (
		!showSkeletons &&
		(!items || items.length === 0 || aiSearchHasFailed || aiResultsNoSearchHits)
	) {
		return null
	}

	const handleAlbumClick = (
		albumNumber: number,
		albumTitle: string,
		index: number
	): void => {
		sendAnalytics({
			noun: 'Album',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				AlbumNo: albumNumber,
				AlbumTitle: albumTitle,
				Position: index + 1,
			},
		})
	}

	return (
		<Container maxWidth="large">
			<Heading level={2} size="S" uppercase>
				{showSkeletons ? <Skeleton width={160} /> : t('top-albums')}
			</Heading>
			{showSkeletons ? (
				<CardListSkeleton scrollable>
					{Array(16)
						.fill(0)
						?.map((_, index) => {
							return (
								<CardSkeleton
									data-testid="skeleton"
									key={`skeleton-${index}`}
									thumbnailHeight={CARD_LIST_THUMBNAIL_MAX_WIDTH}
									thumbnailWidth={CARD_LIST_THUMBNAIL_MAX_WIDTH}
									title
								/>
							)
						})}
				</CardListSkeleton>
			) : (
				<>
					{albums.length > 0 && (
						<CardList scrollable>
							{albums.map((album, index) => {
								const albumUrl = getAlbumUrl(album)
								return (
									<Card
										key={album.number}
										title={album.name}
										href={albumUrl}
										data-testid="album-card"
										onClick={() =>
											handleAlbumClick(album.number, album.name, index)
										}
										togglePlay={
											album.topTrack &&
											(() =>
												togglePlay(album.topTrack, {
													source: 'Album Search',
												}))
										}
										isPlaying={
											isPlayingFromPlayer &&
											album.topTrack?.id === currentTrack.id
										}
										buttonProps={{
											'aria-label':
												isPlayingFromPlayer &&
												album.topTrack?.id === currentTrack.id
													? `pause ${album.name}`
													: `play ${album.name}`,
										}}
										thumbnail={
											album.artworkImageUrl ? (
												<Image
													alt=""
													height={CARD_LIST_THUMBNAIL_MAX_WIDTH}
													src={optimizeContentfulImage(album.artworkImageUrl, {
														width: CARD_LIST_THUMBNAIL_MAX_WIDTH,
														height: CARD_LIST_THUMBNAIL_MAX_WIDTH,
													})}
													width={CARD_LIST_THUMBNAIL_MAX_WIDTH}
												/>
											) : (
												<PlaceholderAlbumArtwork
													className={styles.placeholderAlbumArtwork}
												/>
											)
										}
									/>
								)
							})}
						</CardList>
					)}
				</>
			)}
		</Container>
	)
}

const ConfiguredAlbumResults = ({
	locale,
}: {
	locale: string
}): JSX.Element => {
	const { indexUiState } = useInstantSearch()
	const { nbHits } = useStats()
	const { items } = useInfiniteHits()

	const lyricsRestrictionCommitted = useSearchSelectorStore(
		(state) => state.lyricsRestrictionCommitted
	)

	const sortByMostRecent = !!indexUiState.sortBy

	const configureProps = {
		hitsPerPage: 16,
		restrictSearchableAttributes: lyricsRestrictionCommitted
			? [...getLyricsRestrictedAttributes()]
			: getAlbumRestrictedAttributes(locale),
	} as ConfigureProps

	return (
		<Index
			indexName={`${process.env.NEXT_PUBLIC_ALGOLIA_ALBUMS_INDEX}${
				sortByMostRecent ? '_most_recent' : ''
			}`}
		>
			<Configure {...configureProps} />
			<AlbumResults trackResults={items} nbTrackHits={nbHits} />
		</Index>
	)
}

export default ConfiguredAlbumResults
